//core
import React, {useCallback, useContext, useEffect, useState} from "react";
import { useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import { useTranslation } from "react-i18next";
import arePropsEqual from "react-fast-compare";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Badge,
  Tooltip,
  Collapse,
  useMediaQuery,
} from '@mui/material';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

//utils
import { countValuesInObj } from "../../../../../helpers/countValuesInObj";

//styles
import { useStyles } from "./styles";

//types
import { ISubAffDealsV2FilterDataType } from "../../types";

//routes
import routes from "../../../../../navigation/routes";

//hooks
import { usePermissions } from "../../../../../store/common/configuration/usePermissions";

//context
import {
	GlobalContext,
	initialAdmSubAffDealsV2Filter,
	OrderType
} from "../../../../../context/GlobalContext";

//constants
import { favoriteFilterKeys, reportStorageProps } from "../../constants";

//components
import MuiDrawerWrapper from "../../../../UI/MuiDrawerWrapper";
import Filter from "./components/Filter";
import SelectedFiltersWrapper from "./components/SelectedFiltersWrapper";
import SortMenu from "../../../../common/SortMenu";
import PermissionsWrapper from "../../../../common/PermissionsWrapper";
import VisibleFilter from "./components/VisibleFilter";
import Button from "../../../../UI/Buttons/Button";


interface IDrawerFilterProps {
  initialFilter: ISubAffDealsV2FilterDataType;
  setUpdateFilter: React.Dispatch<React.SetStateAction<ISubAffDealsV2FilterDataType>>;
	children?: React.ReactNode | null;
	order: OrderType;
	setOrder: (order: OrderType) => void;
	onGenerateReport: () => void;
}

const DrawerFilter: React.FC<IDrawerFilterProps> = ({
  initialFilter,
  setUpdateFilter,
  children = null,
  order,
  setOrder,
  onGenerateReport,
}): JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useNavigate();

  const { hasPermissions } = usePermissions();
  const context = useContext(GlobalContext);

  const mobile = useMediaQuery('(max-width:767px)');

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [activeField, setActiveField] = useState('');

  //favorite fields state
  const [favAdmSADPlatformIds, setFavAdmSADPlatformIds] = useLocalStorage<boolean>(favoriteFilterKeys.platformIds, false);
  const [favAdmSADCreatedByIds, setFavAdmSADCreatedByIds] = useLocalStorage<boolean>(favoriteFilterKeys.createdByIds, false);

  const hasFavoriteFields = favAdmSADPlatformIds || favAdmSADCreatedByIds;

  useEffect(() => {
    if (arePropsEqual(initialAdmSubAffDealsV2Filter, initialFilter)) {
      setOpenCollapse(false)
    }
  }, [initialFilter]);

  const handleClearFilters = useCallback(() => {
    setUpdateFilter(initialAdmSubAffDealsV2Filter);
  }, [setUpdateFilter]);

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  }

  const handleSetActiveField = (field: string) => {
    setActiveField(field);
    setOpenDrawer(true);
  }

  const handleToggleCollapse = () => {
    setOpenCollapse(prevState => !prevState);
  }

  const onDeleteOption = useCallback((id: string | number, fieldName: string) => {
    setUpdateFilter( (prevState) => {
	    const newFieldData = reportStorageProps.exceptionsStr.includes(fieldName)
		    // @ts-ignore
		    ? prevState[fieldName].filter((_, i) => i !== indexToRemove)
		    // @ts-ignore
		    : prevState[fieldName].filter((item: any) => item.id !== id);

      context.setAdmSubAffDealsV2Filter({
        ...context.admSubAffDealsV2Filter,
        [fieldName]: newFieldData,
      });

      return {
        ...prevState,
        [fieldName]: newFieldData,
      }
    });
  }, [setUpdateFilter]);

  const collapseButton = (<>
    <Button
      type="button"
      variant="text"
      color="secondary"
      size="small"
      onClick={handleToggleCollapse}
      startIcon={openCollapse ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    >
      {openCollapse
        ? t("common.components.filters.hide")
        : t("common.components.filters.show")}
    </Button>
  </>);

	const handleCreateDeal = useCallback(() => {
		history(routes.admin.sub_aff_deals_v2.create)
	}, []);

  return (<>
    <Box className={styles.block}>
      {!mobile && (
        <Box className={styles.first}>
          { !!children && children }
        </Box>
      )}

      <Box className={styles.second}>
        <Box className={styles.actions}>
          {/*reset filter*/}
          <IconButton
            className={styles.iconButton}
            size={mobile ? 'small' : 'medium'}
            onClick={handleClearFilters}
            disabled={arePropsEqual(initialAdmSubAffDealsV2Filter, initialFilter)}
          >
            <Tooltip title={t("common.buttons.reset_filters")} placement="bottom">
              <FilterAltOffIcon
                color={arePropsEqual(initialAdmSubAffDealsV2Filter, initialFilter) ? 'inherit' : 'primary'}
              />
            </Tooltip>
          </IconButton>

          {/*open filter*/}
          <IconButton
            className={styles.iconButton}
            size={mobile ? 'small' : 'medium'}
            onClick={handleOpenDrawer}
          >
            <Tooltip title={t("common.components.filters.title")} placement="bottom">
              <Badge
                className={styles.badge}
                badgeContent={countValuesInObj(initialFilter)}
                color="secondary"
              >
                <FilterAltIcon
                  color={!!countValuesInObj(initialFilter) ? 'primary' : 'inherit'}
                />
              </Badge>
            </Tooltip>
          </IconButton>

          {/*{!!sortingColumns && !showOrderOnlyMobile && mobile && <SortMenu*/}
          {/*  color="default"*/}
          {/*  columns={sortingColumns}*/}
          {/*  order={order}*/}
          {/*  setOrder={setOrder}*/}
          {/*  noMargin={true}*/}
          {/*  prefix="columns_for_display.table.generate_report."*/}
          {/*/>}*/}
        </Box>
      </Box>
    </Box>

    {!mobile && (
      <Box className={styles.buttonWrapper}>
        <PermissionsWrapper permissions={["api3.admin.subaffiliatedealcontroller.subaffiliate_deal_create"]}>
          <Button
            gradient
            sx={{ width: 'max-content' }}
            variant="contained"
            size={'medium'}
            onClick={handleCreateDeal}
          >
            {t("common.buttons.create")}
          </Button>
        </PermissionsWrapper>

        {(hasFavoriteFields || !!countValuesInObj(initialFilter)) && collapseButton}
      </Box>
    )}

    {!mobile && (
      <Collapse in={openCollapse} collapsedSize={0}>
        <VisibleFilter
          filter={initialFilter}
          setUpdateFilter={setUpdateFilter}
        />

        {!!countValuesInObj(initialFilter) && (
          <SelectedFiltersWrapper
            initialFilter={initialFilter}
            setActiveField={handleSetActiveField}
            onDeleteOption={onDeleteOption}
          />
        )}
      </Collapse>
    )}

    <MuiDrawerWrapper
      title={t("common.components.filters.title")}
      open={openDrawer}
      toggleDrawerState={setOpenDrawer}
    >
      <Filter
        activeField={activeField}
        setActiveField={setActiveField}
        initialFilter={initialFilter}
        setUpdateFilter={setUpdateFilter}
        setToggleDrawer={setOpenDrawer}
      />
    </MuiDrawerWrapper>
  </>);
};

export default DrawerFilter;
